/**
 * A busca ao vivo no SGR enquanto a pessoa digita (spec 032, §3.1) — o campo
 * da placa do "Novo ticket" e o campo Recurso do ticket.
 *
 * PURO de propósito (sem React e sem alias `@/`): o web não tem executor de
 * testes, e a prova mora na API (apps/api/src/shared/sgr-search-controller.test.ts),
 * com relógio falso — como a ficha (ticket-sheet-view) e o histórico.
 *
 * - dispara com 3+ letras ou dígitos depois de normalizar, com PAUSA de 400 ms
 *   depois da última tecla (a consulta custa ~1,8 s com 3 ou 7 caracteres; uma
 *   pausa curta faria uma consulta por letra de quem digita devagar);
 * - a consulta anterior é CANCELADA (AbortController) quando sai outra, e o
 *   servidor repassa o cancelamento ao SGR;
 * - ESTREITAMENTO local: o SGR busca por substring, e o resultado de "QXD1"
 *   está dentro do de "QXD". Com o de "QXD" COMPLETO na memória do campo, as
 *   teclas que estendem o texto filtram aqui, sem consulta — pela MESMA regra
 *   do @erp/shared (sgrItemMatches), senão a lista mudaria conforme a
 *   velocidade de quem digita;
 * - apagar volta ao que já estava na memória do campo;
 * - "Buscando…" só depois de 300 ms.
 *
 * Uma placa digitada custa, no caso comum, UMA consulta ao SGR.
 */
import {
  classifySgrSearch,
  SGR_SEARCH_DEBOUNCE_MS,
  SGR_SEARCH_PAGE,
  SGR_SEARCH_SPINNER_DELAY_MS,
  SGR_SEARCH_VISIBLE,
  sgrItemMatches,
  sgrMatchOf,
  sgrQueryNarrows,
  sortSgrVehicles,
  type SgrCustomerItem,
  type SgrSearchBody,
  type SgrSearchQuery,
  type SgrSearchResponse,
  type SgrUnavailableReason,
  type SgrVehicleItem,
  type Terms,
} from '@erp/shared';
import { unavailableText } from './sgr-view';

export type SgrSearchResult =
  | { kind: 'vehicles'; items: SgrVehicleItem[]; total: number; complete: boolean; homonyms?: boolean }
  | { kind: 'customers'; items: SgrCustomerItem[]; total: number }
  | { kind: 'unavailable'; reason: SgrUnavailableReason | 'not_available' };

export interface SgrSearchState {
  /** idle: nada a buscar · waiting: na pausa · loading: consultando · done: resultado */
  phase: 'idle' | 'waiting' | 'loading' | 'done';
  /** o "Buscando…" (só depois de 300 ms de consulta) */
  spinner: boolean;
  /** o texto normalizado de agora (null = menos de 3 letras ou dígitos) */
  query: SgrSearchQuery | null;
  result: SgrSearchResult | null;
  /** de onde veio o resultado: uma consulta, a memória do campo ou o estreitamento local */
  origin: 'request' | 'memory' | 'narrowed' | null;
  /** os veículos de UMA pessoa (escolhida na busca por CPF) */
  owner: string | null;
}

/** a consulta ao servidor; o chamador transforma erro de rede/404 em `unavailable` */
export type SgrSearchFn = (body: SgrSearchBody, signal: AbortSignal) => Promise<SgrSearchResponse>;

export interface SgrSearchControllerOptions {
  search: SgrSearchFn;
  onChange: (state: SgrSearchState) => void;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (handle: unknown) => void;
  debounceMs?: number;
  spinnerDelayMs?: number;
}

export interface SgrSearchController {
  /** cada tecla */
  input(text: string): void;
  /** escolher uma pessoa da busca por CPF: os veículos dela (sem pausa) */
  pickCustomer(name: string): void;
  /** o campo fechou: nada pendente, nada em voo */
  dispose(): void;
  state(): SgrSearchState;
}

export const IDLE_SGR_SEARCH: SgrSearchState = {
  phase: 'idle',
  spinner: false,
  query: null,
  result: null,
  origin: null,
  owner: null,
};

const keyOf = (q: SgrSearchQuery, owner = false) => `${owner ? 'owner' : q.kind}:${q.q}`;

/** a resposta do servidor → o resultado do campo (lista branca: o que não é `ok` é indisponível) */
function toResult(res: SgrSearchResponse): SgrSearchResult {
  if (res.status === 'ok' && res.kind === 'vehicles') {
    return { kind: 'vehicles', items: res.items, total: res.total, complete: res.complete, homonyms: res.homonyms };
  }
  if (res.status === 'ok' && res.kind === 'customers') return { kind: 'customers', items: res.items, total: res.total };
  return { kind: 'unavailable', reason: res.status === 'unavailable' ? res.reason : 'not_available' };
}

/** estreita um resultado COMPLETO para o texto novo, com a ordem refeita para ele */
export function narrowSgrResult(
  base: Extract<SgrSearchResult, { kind: 'vehicles' }>,
  next: SgrSearchQuery
): Extract<SgrSearchResult, { kind: 'vehicles' }> {
  const items = sortSgrVehicles(
    base.items.filter((item) => sgrItemMatches(item, next)).map((item) => ({ ...item, match: sgrMatchOf(item.plate, next) }))
  );
  return { kind: 'vehicles', items, total: items.length, complete: true };
}

export function createSgrSearchController(opts: SgrSearchControllerOptions): SgrSearchController {
  const setTimer = opts.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
  const clearTimer = opts.clearTimer ?? ((h) => clearTimeout(h as ReturnType<typeof setTimeout>));
  const debounceMs = opts.debounceMs ?? SGR_SEARCH_DEBOUNCE_MS;
  const spinnerDelayMs = opts.spinnerDelayMs ?? SGR_SEARCH_SPINNER_DELAY_MS;
  /** a memória do campo: o que já foi consultado, pela chave do texto */
  const memory = new Map<string, { query: SgrSearchQuery; result: SgrSearchResult }>();
  let state: SgrSearchState = IDLE_SGR_SEARCH;
  let debounce: unknown = null;
  let spinner: unknown = null;
  let inflight: AbortController | null = null;
  let seq = 0;

  const emit = (next: SgrSearchState) => {
    state = next;
    opts.onChange(state);
  };

  const cancel = () => {
    if (debounce !== null) clearTimer(debounce);
    if (spinner !== null) clearTimer(spinner);
    debounce = null;
    spinner = null;
    inflight?.abort();
    inflight = null;
    seq += 1;
  };

  /** um resultado completo na memória que contém o texto novo? */
  function narrowFromMemory(query: SgrSearchQuery): SgrSearchResult | null {
    for (const entry of [...memory.values()].reverse()) {
      const r = entry.result;
      if (r.kind !== 'vehicles' || !r.complete || entry.query.kind !== query.kind) continue;
      if (!sgrQueryNarrows(entry.query, query)) continue;
      return narrowSgrResult(r, query);
    }
    return null;
  }

  async function run(query: SgrSearchQuery, owner: string | null) {
    const mySeq = seq;
    const ctrl = new AbortController();
    inflight = ctrl;
    emit({ ...state, phase: 'loading', spinner: false, query, owner });
    spinner = setTimer(() => {
      spinner = null;
      if (mySeq === seq) emit({ ...state, spinner: true });
    }, spinnerDelayMs);
    let res: SgrSearchResponse;
    try {
      res = await opts.search(owner ? { q: owner, owner: true } : { q: query.q }, ctrl.signal);
    } catch {
      res = { status: 'unavailable', reason: 'network_error' };
    }
    // uma consulta mais nova (ou o fim do campo) passou na frente: esta não vale
    if (mySeq !== seq || ctrl.signal.aborted) return;
    if (spinner !== null) clearTimer(spinner);
    spinner = null;
    inflight = null;
    const result = toResult(res);
    // indisponível não fica na memória: a próxima tecla tenta de novo
    if (result.kind !== 'unavailable') memory.set(keyOf(query, owner !== null), { query, result });
    emit({ phase: 'done', spinner: false, query, result, origin: 'request', owner });
  }

  return {
    input(text) {
      cancel();
      const query = classifySgrSearch(text);
      if (!query) {
        emit(IDLE_SGR_SEARCH);
        return;
      }
      const remembered = memory.get(keyOf(query));
      if (remembered) {
        emit({ phase: 'done', spinner: false, query, result: remembered.result, origin: 'memory', owner: null });
        return;
      }
      const narrowed = narrowFromMemory(query);
      if (narrowed) {
        emit({ phase: 'done', spinner: false, query, result: narrowed, origin: 'narrowed', owner: null });
        return;
      }
      emit({ ...IDLE_SGR_SEARCH, phase: 'waiting', query, result: state.result, origin: state.origin });
      debounce = setTimer(() => {
        debounce = null;
        void run(query, null);
      }, debounceMs);
    },

    pickCustomer(name) {
      cancel();
      const q = name.trim().replace(/\s+/g, ' ').toUpperCase();
      const query: SgrSearchQuery = { kind: 'name', q };
      const remembered = memory.get(keyOf(query, true));
      if (remembered) {
        emit({ phase: 'done', spinner: false, query, result: remembered.result, origin: 'memory', owner: q });
        return;
      }
      void run(query, q);
    },

    dispose() {
      cancel();
    },

    state: () => state,
  };
}

/** as linhas que a tela mostra (até 10) */
export function visibleSgrItems<T>(items: readonly T[]): T[] {
  return items.slice(0, SGR_SEARCH_VISIBLE);
}

/** "Muitos resultados — continue digitando": mais de 50 com só 3 caracteres */
export function sgrTooMany(state: SgrSearchState): boolean {
  return (
    state.result?.kind === 'vehicles' &&
    state.query?.kind === 'plate' &&
    state.query.q.length === 3 &&
    state.result.total > SGR_SEARCH_PAGE
  );
}

export interface SgrSearchStatus {
  text: string;
  tone: 'info' | 'warn';
  spinner?: boolean;
}

/**
 * A linha de estado debaixo do campo (spec 032, §3.1), com os textos da spec:
 * "Nenhum veículo com essa placa no SGR — preencha a ficha à mão", "SGR
 * indisponível — preencha a ficha à mão" e "Muitos resultados — digite mais da
 * placa" (total > 50 com 3 caracteres). O recurso e a placa vêm dos termos do
 * tenant; o nome da origem, da API.
 */
export function sgrSearchStatus(
  state: SgrSearchState,
  sourceName: string,
  terms: Pick<Terms, 'resource' | 'resourceId' | 'contact'>
): SgrSearchStatus | null {
  if (state.phase === 'loading' && state.spinner) return { text: `Buscando no ${sourceName}…`, tone: 'info', spinner: true };
  const r = state.result;
  if (state.phase !== 'done' || !r) return null;
  if (r.kind === 'unavailable') return { text: unavailableText(sourceName, r.reason), tone: 'warn' };
  if (r.kind === 'customers') {
    return r.items.length === 0
      ? { text: `Nenhum ${terms.contact} com esse CPF no ${sourceName} — preencha a ficha à mão.`, tone: 'info' }
      : { text: `Escolha o ${terms.contact} para ver os ${terms.resource}s dele.`, tone: 'info' };
  }
  if (r.items.length === 0) {
    if (state.query?.kind === 'plate') {
      return { text: `Nenhum ${terms.resource} com essa ${terms.resourceId} no ${sourceName} — preencha a ficha à mão.`, tone: 'info' };
    }
    return { text: `Nada encontrado no ${sourceName} — preencha a ficha à mão.`, tone: 'info' };
  }
  if (sgrTooMany(state)) return { text: `Muitos resultados — digite mais da ${terms.resourceId}.`, tone: 'info' };
  if (r.items.length > visibleSgrItems(r.items).length) {
    return { text: `Mostrando ${visibleSgrItems(r.items).length} de ${r.total} — continue digitando.`, tone: 'info' };
  }
  return null;
}
